<script lang="ts" setup>
import { ref, nextTick } from 'vue'
import { useNuxtApp } from '#app'
import { type Specialisation } from '@/models'

const props = defineProps<{ allSpecialisations: Specialisation[] }>()
const { $recaptcha } = useNuxtApp()
const router = useRouter()
const { t } = useI18n()
const localePath = useLocalePath()

const SHOW_RATE = 0.2
const recaptchaSolved = ref(false) // Don't ask again in same session.

onMounted(() => {
  recaptchaSolved.value = sessionStorage.getItem('captcha-solved') === '1'
})

const getAdviceText = ref('')
const showWidget = ref(false)
const isSubmitting = ref(false)
const recaptchaCompleted = ref(false)

async function submit() {
  // If already submitting, do nothing
  if (isSubmitting.value) return

  isSubmitting.value = true
  try {
    // mapping all specialisations Ids and Names to single string.
    const concatenatedSpecialisations = props.allSpecialisations.map(s => `${s.id} ${s.name}`).join('\n')

    // Calling our AI module
    const response = await fetch(`/api/ai/search-lawyer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({userText: getAdviceText.value, specialisations: concatenatedSpecialisations})
    })
    
    // Setting user text to session storage
    sessionStorage.setItem('smart-search-request', getAdviceText.value)

    const result = await response.json()
    const specialisationSlugs = getSpecialisationSlugs(result.data)
    navigateToLawyersPage(specialisationSlugs)
  } finally {
    setTimeout(() => {
      isSubmitting.value = false
    }, 1000)
  }
}

function showCaptcha() {
  showWidget.value = true
  nextTick(() => {
    const el = document.getElementById('recaptcha-widget')
    if (!el) return console.warn('reCAPTCHA container not found')
    $recaptcha.render('recaptcha-widget', (token: string) => {
      recaptchaCompleted.value = true
      recaptchaSolved.value   = true
      sessionStorage.setItem('captcha-solved', '1')
      submit()
    })
  })
}

function handleSend() {
  // length or in-flight guard
  if (getAdviceText.value.length < 20 || isSubmitting.value) return

  // if they’ve already solved once → go straight
  if (recaptchaSolved.value) {
    return submit()
  }

  // roll the dice
  if (Math.random() < SHOW_RATE) {
    showCaptcha()
  } else {
    submit()
  }
}

// Getting specialisations slugs from ids list that we got from chatGpt
function getSpecialisationSlugs(ids: any) {
    const specialisationSlugs = ids.map((id: number) => {
      const specialisation = props.allSpecialisations.find((specialisation: Specialisation) => specialisation.id === id)
      return specialisation ? specialisation.slug : null
    }).filter((slug : any) => slug !== null)

    return specialisationSlugs
}

function navigateToLawyersPage(specialisationSlugs: Array<string>) {
  // Constructing query filters.
  const query: Record<string, any> = { page: 1 }
  if (specialisationSlugs.length > 0) {
    query.specialisation = specialisationSlugs // pass the array directly to get repeated query parameters
  }

  //Navigating to lawyers page.
  router.push(
    localePath({
      name: 'lawyers-specialty',
      params: { specialty: t('advocates').toLowerCase() },
      query
    })
  )
}
</script>
<template>
  <div class="bg-white h-[460px] lg:h-[205px] rounded-[20px] relative ">
    <div v-if="showWidget && !recaptchaCompleted" class="w-full h-full flex justify-center items-center absolute inset-0">
      <div id="recaptcha-widget"></div>
    </div>
    <div class="mx-6 lg:mx-0 overflow-hidden lg:w-full lg:flex lg:h-full">
      <div class="mt-3 lg:mt-0 overflow-hidden rounded-[20px] lg:rounded-none lg:w-3/4">
        <textarea
          v-model="getAdviceText"
          :placeholder="$t('describe_your_problem')"
          class="px-3 lg:px-6 py-2.5 text-darkBlue h-[350px] lg:h-full w-full rounded-[20px] lg:rounded-r-none resize-none focus:outline-none scrollbar scrollbar--round scrollbar--dark border border-gray-300 lg:border-none"
        />
      </div>
      <div class="lg:my-3 lg:border lg:border-r-1 lg:border-white-600" />
      <div class="lg:flex lg:flex-col lg:w-1/4 justify-center">
        <div class="flex justify-center mt-8 lg:mt-auto mb-3">
          <ClientOnly>
            <el-tooltip placement="top" :content="!recaptchaCompleted ? $t('complete_recaptcha') : $t('description_too_short')" :disabled="getAdviceText.length > 20" effect="blue">
              <button class="bg-darkBlue hover:bg-lightBlue w-full lg:w-5/6 h-11 text-white font-semibold rounded-full transition-all duration-50 active:scale-95 disabled:opacity-70 disabled:grayscale disabled:cursor-not-allowed" :disabled="getAdviceText.length < 20" @click="handleSend">
                {{$t('search')}}
              </button>
            </el-tooltip>
          </ClientOnly>
        </div>
      </div>
    </div>
  </div>
</template>